import React, { useState, useEffect } from 'react';
import { supabase } from '../../../lib/supabase';
import { useAuth } from '../../../contexts/AuthContext';
import Icon from '../../../components/AppIcon';
import ProgressChart from '../../progress-analytics/components/ProgressChart';

const FeedbackAnalytics = () => {
  const { user, currentTenant } = useAuth();
  const [feedbackData, setFeedbackData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [timeRange, setTimeRange] = useState('30');

  useEffect(() => {
    loadFeedbackData();
  }, [user, currentTenant, timeRange]);

  const loadFeedbackData = async () => {
    if (!user?.id || !currentTenant?.id) return;

    setLoading(true);
    try {
      const daysAgo = new Date();
      daysAgo?.setDate(daysAgo?.getDate() - parseInt(timeRange));

      const { data, error } = await supabase
        ?.from('workout_feedback')
        ?.select('*')
        ?.eq('user_id', user?.id)
        ?.eq('tenant_id', currentTenant?.id)
        ?.gte('created_at', daysAgo?.toISOString())
        ?.order('created_at', { ascending: true });

      if (error) throw error;
      setFeedbackData(data || []);
    } catch (error) {
      console.error('Error loading feedback data:', error);
    } finally {
      setLoading(false);
    }
  };

  const calculateMetrics = () => {
    if (feedbackData?.length === 0) {
      return {
        avgRating: 0,
        totalFeedback: 0,
        satisfactionRate: 0,
        mostCommonDifficulty: 'N/A'
      };
    }

    const avgRating = feedbackData?.reduce((sum, f) => sum + (f?.rating || 0), 0) / feedbackData?.length;
    const satisfactionRate = (feedbackData?.filter(f => f?.rating >= 4)?.length / feedbackData?.length) * 100;
    
    const difficultyCount = {};
    feedbackData?.forEach(f => {
      if (f?.difficulty_level) {
        difficultyCount[f?.difficulty_level] = (difficultyCount?.[f?.difficulty_level] || 0) + 1;
      }
    });
    const mostCommonDifficulty = Object?.keys(difficultyCount)?.length > 0
      ? Object?.keys(difficultyCount)?.reduce((a, b) => difficultyCount?.[a] > difficultyCount?.[b] ? a : b)
      : 'N/A';

    return {
      avgRating: avgRating?.toFixed(1),
      totalFeedback: feedbackData?.length,
      satisfactionRate: satisfactionRate?.toFixed(0),
      mostCommonDifficulty: mostCommonDifficulty?.replace('_', ' ')?.replace(/\b\w/g, l => l?.toUpperCase())
    };
  };

  const getChartData = () => {
    if (feedbackData?.length === 0) return [];

    const groupedByDate = {};
    feedbackData?.forEach(feedback => {
      const date = new Date(feedback?.created_at)?.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
      if (!groupedByDate?.[date]) {
        groupedByDate[date] = { date, ratings: [], count: 0 };
      }
      groupedByDate?.[date]?.ratings?.push(feedback?.rating);
      groupedByDate[date].count++;
    });

    return Object?.values(groupedByDate)?.map(group => ({
      name: group?.date,
      rating: (group?.ratings?.reduce((a, b) => a + b, 0) / group?.ratings?.length)?.toFixed(1),
      count: group?.count
    }));
  };

  const getRatingDistribution = () => {
    const distribution = { 1: 0, 2: 0, 3: 0, 4: 0, 5: 0 };
    feedbackData?.forEach(f => {
      if (f?.rating) distribution[f?.rating]++;
    });
    return Object?.entries(distribution)?.map(([rating, count]) => ({
      name: `${rating} Star`,
      count
    }));
  };

  const metrics = calculateMetrics();
  const chartData = getChartData();
  const ratingDistribution = getRatingDistribution();

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="text-center">
          <Icon name="Loader" size={32} className="text-primary animate-spin mx-auto mb-2" />
          <p className="text-muted-foreground">Loading analytics...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Time Range Filter */}
      <div className="flex items-center justify-between">
        <h2 className="text-xl font-semibold text-foreground">Feedback Analytics</h2>
        <select
          value={timeRange}
          onChange={(e) => setTimeRange(e?.target?.value)}
          className="px-4 py-2 bg-card border border-border rounded-lg text-foreground focus:outline-none focus:ring-2 focus:ring-primary"
        >
          <option value="7">Last 7 days</option>
          <option value="30">Last 30 days</option>
          <option value="90">Last 90 days</option>
        </select>
      </div>

      {/* Metrics Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        <div className="bg-card border border-border rounded-lg p-5">
          <div className="flex items-center gap-3 mb-2">
            <div className="w-10 h-10 rounded-lg bg-warning/20 flex items-center justify-center">
              <Icon name="Star" size={20} className="text-warning" />
            </div>
            <div>
              <p className="text-sm text-muted-foreground">Avg Rating</p>
              <p className="text-2xl font-bold text-foreground">{metrics?.avgRating}</p>
            </div>
          </div>
        </div>

        <div className="bg-card border border-border rounded-lg p-5">
          <div className="flex items-center gap-3 mb-2">
            <div className="w-10 h-10 rounded-lg bg-primary/20 flex items-center justify-center">
              <Icon name="MessageSquare" size={20} className="text-primary" />
            </div>
            <div>
              <p className="text-sm text-muted-foreground">Total Feedback</p>
              <p className="text-2xl font-bold text-foreground">{metrics?.totalFeedback}</p>
            </div>
          </div>
        </div>

        <div className="bg-card border border-border rounded-lg p-5">
          <div className="flex items-center gap-3 mb-2">
            <div className="w-10 h-10 rounded-lg bg-success/20 flex items-center justify-center">
              <Icon name="TrendingUp" size={20} className="text-success" />
            </div>
            <div>
              <p className="text-sm text-muted-foreground">Satisfaction</p>
              <p className="text-2xl font-bold text-foreground">{metrics?.satisfactionRate}%</p>
            </div>
          </div>
        </div>

        <div className="bg-card border border-border rounded-lg p-5">
          <div className="flex items-center gap-3 mb-2">
            <div className="w-10 h-10 rounded-lg bg-accent/20 flex items-center justify-center">
              <Icon name="Activity" size={20} className="text-accent" />
            </div>
            <div>
              <p className="text-sm text-muted-foreground">Difficulty</p>
              <p className="text-lg font-bold text-foreground">{metrics?.mostCommonDifficulty}</p>
            </div>
          </div>
        </div>
      </div>

      {/* Charts */}
      {feedbackData?.length > 0 ? (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          <ProgressChart
            title="Rating Trends"
            data={chartData}
            type="line"
            dataKeys={[{ dataKey: 'rating', name: 'Avg Rating' }]}
            colors={['var(--color-primary)']}
          />
          <ProgressChart
            title="Rating Distribution"
            data={ratingDistribution}
            type="bar"
            dataKeys={[{ dataKey: 'count', name: 'Feedback Count' }]}
            colors={['var(--color-accent)']}
          />
        </div>
      ) : (
        <div className="bg-card border border-border rounded-lg p-12 text-center">
          <Icon name="BarChart3" size={48} className="text-muted-foreground mx-auto mb-4" />
          <h3 className="text-lg font-semibold text-foreground mb-2">No Feedback Data Yet</h3>
          <p className="text-muted-foreground">Submit workout feedback to see your analytics here</p>
        </div>
      )}
    </div>
  );
};

export default FeedbackAnalytics;